import {
  Modal,
  Form,
  Input,
  InputNumber,
  Spin,
  Button,
  notification,
} from "antd";
import React, { useEffect, useState } from "react";
import { getUserPlan, updatePlanExpiry } from "../../Services/subscription";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import dayjs from "dayjs";
import { formatDate } from "../../utils/date";

function PlanViewModal({ isModalVisible, handleCancel, initialValues }) {
  const [form] = Form.useForm();
  const [increaseDays, setIncreaseDays] = useState(null);
  const [userSubscriptionId, setUserSubscriptionId] = useState(0);
  const [currentExpiry, setCurrentExpiry] = useState(null);
  const [newExpiry, setNewExpiry] = useState(null);
  const queryClient = useQueryClient(); // add this line

  const {
    data,
    isLoading,
  } = useQuery({
    queryKey: [`user-plan-${initialValues?.id}`],
    queryFn: () => getUserPlan(initialValues?.id),
    enabled: !!initialValues?.id && isModalVisible, // only fetch when modal is open with a valid id
  });

  useEffect(() => {
    if (data) {
      if (data.id) {
        setUserSubscriptionId(data.id);
      }
      if (data.expire_datetime) {
        setCurrentExpiry(dayjs(data.expire_datetime));
      } else {
        setCurrentExpiry(null);
      }
    }

    if (isModalVisible) {
      form.resetFields();
      setIncreaseDays(null);
      setNewExpiry(null);
    }
  }, [data, isModalVisible, form]);

  useEffect(() => {
    if (currentExpiry && increaseDays >= 1) {
      const updated = currentExpiry.add(increaseDays, "day");
      setNewExpiry(updated);
    } else {
      setNewExpiry(null);
    }
  }, [increaseDays, currentExpiry]);

  const handleFinish = async (values) => {
    try {
      const response = await updatePlanExpiry(
        initialValues?.id,
        values.expiry_days,
        userSubscriptionId
      );
      if (response) {
        notification.success({
          message: "Plan Expiry Updated",
        });
        queryClient.invalidateQueries(["users"]); 
        handleCancel();
      }
    } catch (error) {
      notification.error({
        message: "Update Failed",
        description: error.message,
      });
    }
  };

  return (
    <>
      <style>
        {`
          .custom-expiry-item {
            padding-top: 10px !important;
            padding-bottom: 0px !important;
          }
        `}
      </style>
      <Modal
        title="User Plan Details"
        open={isModalVisible}
        footer={null}
        onCancel={handleCancel}
      >
        {isLoading ? (
          <Spin />
        ) : data ? (
          <Form
            layout="vertical"
            form={form}
            onFinish={handleFinish}
            onValuesChange={(changedValues) => {
              if ("expiry_days" in changedValues) {
                setIncreaseDays(changedValues.expiry_days);
              }
            }}
          >
            <p>
              <strong>Plan Name:</strong> {data.name || "-"}
            </p>
            <p>
              <strong>Current Expiry Date:</strong>{" "}
              {data.expire_datetime
                ? formatDate(data.expire_datetime)
                : (data.is_default_plan ? "Nil" : "-")}
            </p>

            <Form.Item
              label="Increase Expiry Date by (Days)"
              name="expiry_days"
              rules={[
                { required: true, message: "Please enter expiry days" },
              ]}
              className="custom-expiry-item"
            >
              <Input.Group compact>
                <InputNumber
                  min={1}
                  max={365}
                  style={{ width: "85%" }}
                  placeholder="Expiry Days"
                  value={increaseDays}
                  onChange={(value) => {
                    const safeValue = Math.min(value || 0, 365);
                    form.setFieldsValue({ expiry_days: safeValue });
                    setIncreaseDays(safeValue);
                  }}
                />
                <Input
                  style={{
                    width: 60,
                    textAlign: "center",
                    pointerEvents: "none",
                    backgroundColor: "#f5f5f5",
                    fontWeight: "bold",
                    color: "black"
                  }}
                  value="Days"
                  disabled
                />
              </Input.Group>
            </Form.Item>

            {newExpiry && (
              <p>
                <strong>New Expiry Date:</strong>{" "}
                {formatDate(newExpiry.format("YYYY-MM-DD HH:mm:ss"))}
              </p>
            )}

            <Form.Item className="flex justify-end">
              <Button type="primary" htmlType="submit">
                Update
              </Button>
            </Form.Item>
          </Form>
        ) : null}
      </Modal>
    </>
  );
}

export default PlanViewModal;
